const creditsFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })

export interface AdminCostValues {
  llmUsd: number | undefined
  ttsCredits: number | undefined
  ttsUsd: number | undefined
  totalUsd: number | undefined
  elevenLabsUsdPer1kCredits: number | undefined
}

export interface AdminCostCompleteness {
  llmUsd: boolean
  ttsCredits: boolean
  ttsUsd: boolean
  totalUsd: boolean
}

export interface AggregatedCallCosts extends AdminCostValues {
  completeness: AdminCostCompleteness
}

export interface ElevenLabsCostInput {
  credits: number | undefined
  usd?: number
  rate?: number
  unit?: 'short' | 'long'
  sourceComplete?: boolean
}

export interface ElevenLabsCostPresentation {
  text: string
  title: string
  complete: boolean
}

interface CostTrace {
  llmCostUsd?: number
  ttsCredits?: number
  ttsUsd?: number
  totalUsd?: number
}

interface CostedTurn {
  role: 'agent' | 'human'
  trace?: CostTrace
}

export function readAdminCosts(...sources: unknown[]): AdminCostValues {
  return {
    llmUsd: firstNumber(sources, 'llmUsd'),
    ttsCredits: firstNumber(sources, 'ttsCredits'),
    ttsUsd: firstNumber(sources, 'ttsUsd'),
    totalUsd: firstNumber(sources, 'totalUsd'),
    elevenLabsUsdPer1kCredits: firstNumber(sources, 'elevenLabsUsdPer1kCredits'),
  }
}

export function aggregateCallCosts(
  turns: CostedTurn[],
  elevenLabsUsdPer1kCredits: number | undefined,
): AggregatedCallCosts {
  const agentTurns = turns.filter((turn) => turn.role === 'agent')
  const traces = agentTurns.flatMap((turn) => (turn.trace ? [turn.trace] : []))
  const expectedTraceCount = agentTurns.length
  const llmUsd = aggregateTraceField(traces, 'llmCostUsd', true, expectedTraceCount)
  const ttsCredits = aggregateTraceField(traces, 'ttsCredits', false, expectedTraceCount)
  const ttsUsd = aggregateTraceField(traces, 'ttsUsd', true, expectedTraceCount)
  const totalUsd = aggregateTraceField(traces, 'totalUsd', true, expectedTraceCount)

  return {
    llmUsd: llmUsd.value,
    ttsCredits: ttsCredits.value,
    ttsUsd: ttsUsd.value,
    totalUsd: totalUsd.value,
    elevenLabsUsdPer1kCredits: finiteNumber(elevenLabsUsdPer1kCredits),
    completeness: {
      llmUsd: llmUsd.complete,
      ttsCredits: ttsCredits.complete,
      ttsUsd: ttsUsd.complete,
      totalUsd: totalUsd.complete,
    },
  }
}

export function formatElevenLabsCost({
  credits,
  usd,
  rate,
  unit = 'short',
  sourceComplete = true,
}: ElevenLabsCostInput): ElevenLabsCostPresentation {
  const validCredits = finiteNumber(credits)
  const validRate = finiteNumber(rate)
  const validUsd = finiteNumber(usd)
  const estimate =
    validUsd ??
    (validCredits !== undefined && validRate !== undefined
      ? (validCredits * validRate) / 1000
      : undefined)
  const creditsText = validCredits === undefined ? '—' : creditsFormatter.format(validCredits)
  const unitText = unit === 'long' ? 'кредитов' : 'кр.'
  const hasPartialValue = !sourceComplete && (validCredits !== undefined || estimate !== undefined)
  const rateTitle =
    validRate === undefined
      ? 'Тариф ElevenLabs не передан API'
      : `Оценка по тарифу: ${formatUsdRate(validRate)} за 1000 кредитов`

  return {
    text: `${creditsText} ${unitText} ${formatApproxUsd(estimate)}${
      hasPartialValue ? ' · неполные данные' : ''
    }`,
    title: hasPartialValue
      ? `${rateTitle}. Не все реплики содержат данные стоимости.`
      : rateTitle,
    complete:
      sourceComplete &&
      validCredits !== undefined &&
      estimate !== undefined &&
      validRate !== undefined,
  }
}

export function formatApproxUsd(value: number | undefined): string {
  const valid = finiteNumber(value)
  return valid === undefined ? '≈ $—' : `≈ ${formatUsd(valid)}`
}

export function formatCallCostUsd(
  value: number | undefined,
  complete: boolean,
  approximate = false,
): string {
  const valid = finiteNumber(value)
  if (valid === undefined) return approximate ? '≈ $—' : '$—'
  if (!complete) return `${formatApproxUsd(valid)} · неполные данные`
  return approximate ? formatApproxUsd(valid) : formatUsd(valid)
}

export function formatUsd(value: number): string {
  const digits = Math.abs(value) > 0 && Math.abs(value) < 0.01 ? 4 : 2
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
}

export function formatUsdRate(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value)
}

function firstNumber(sources: unknown[], key: string): number | undefined {
  for (const source of sources) {
    if (!isRecord(source)) continue
    const value = finiteNumber(source[key])
    if (value !== undefined) return value
  }
  return undefined
}

function aggregateTraceField(
  traces: CostTrace[],
  key: keyof CostTrace,
  round: boolean,
  expectedCount: number,
): { value: number | undefined; complete: boolean } {
  let value = 0
  let present = 0

  for (const trace of traces) {
    const next = finiteNumber(trace[key])
    if (next === undefined) continue
    value += next
    present += 1
  }

  return {
    value: present === 0 ? undefined : round ? roundUsd(value) : value,
    complete: expectedCount > 0 && present === expectedCount,
  }
}

function roundUsd(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
